import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { adminFetch } from '@/utils/adminAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'mobile-studio';

const FIELDS = [
  { group: 'Login screen', items: [
    { path: 'login.title', label: 'Title', fallback: 'Mobile Studio' },
    { path: 'login.tagline', label: 'Tagline', fallback: 'Professional Multi-Track Recording' },
  ]},
  { group: 'Header', items: [
    { path: 'header.title', label: 'Header title', fallback: 'Mobile Studio' },
  ]},
  { group: 'Camera', items: [
    { path: 'camera.notConnectedTitle', label: 'Camera not connected heading', fallback: 'Camera Not Connected' },
  ]},
  { group: 'Backgrounds', items: [
    { path: 'backgrounds.solidTitle', label: 'Solid colors heading', fallback: 'Solid Colors' },
    { path: 'backgrounds.aiTitle', label: 'AI generated heading', fallback: 'AI Generated (tap to create)' },
  ]},
];

const getAt = (obj, path) => {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), obj);
};

const setAt = (obj, path, value) => {
  const parts = path.split('.');
  const clone = { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    cur[p] = { ...(cur[p] || {}) };
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

export default function AdminMobileStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/mobile-studio';

  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Access Denied: Admin only');
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/page-content/${SLUG}`);
        const data = await r.json();
        setContent(data.content || {});
      } catch {
        toast.error('Failed to load page content');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (path, value) => {
    setContent((prev) => setAt(prev, path, value));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`/admin/page-content/${SLUG}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Saved!');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      <header className="bg-stone-900 border-b border-stone-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={backTo} className="p-2 hover:bg-stone-800 rounded-lg" data-testid="admin-mobile-studio-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Video className="w-7 h-7 text-amber-500" />
                Mobile Studio Editor
              </h1>
              <p className="text-sm text-stone-400">Edit the text that appears on /mobile-studio</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="admin-mobile-studio-save"
            className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold rounded-lg flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {loading ? (
          <p className="text-stone-400">Loading…</p>
        ) : (
          FIELDS.map((group) => (
            <section key={group.group} className="space-y-4">
              <h2 className="text-lg font-bold text-amber-400">{group.group}</h2>
              <div className="space-y-4">
                {group.items.map((f) => {
                  const current = getAt(content, f.path);
                  return (
                    <div key={f.path}>
                      <label className="block text-sm text-stone-300 mb-2">{f.label}</label>
                      <input
                        type="text"
                        value={current ?? ''}
                        placeholder={f.fallback}
                        onChange={(e) => handleChange(f.path, e.target.value)}
                        data-testid={`field-${f.path}`}
                        className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:border-amber-500 focus:outline-none"
                      />
                      <p className="text-xs text-stone-500 mt-1">Fallback: {f.fallback}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}
